import { useContext, useEffect } from "react";
import { Routes, Route, Navigate } from 'react-router-dom';
import LoginPage from './features/authentication/LoginPage';
import EnhancedLoginPage from './features/authentication/EnhancedLoginPage';
import PasswordExpiredPage from './features/authentication/PasswordExpiredPage';
import SecurityManagementPage from './features/system/SecurityManagementPage';
import PasswordPolicyPage from './features/system/PasswordPolicyPage';
import CalibrationPolicyPage from './features/system/CalibrationPolicyPage';
import SystemAlertManagementPage from './features/system/SystemAlertManagementPage';
import MobileCodePolicyPage from './features/system/MobileCodePolicyPage';
import SecurityMonitoring from './features/system/SecurityMonitoring';
import AdminAuditLogPage from './features/system/AdminAuditLogPage';
import AuditCapacityMonitoring from "./features/system/AuditCapacityMonitoring";
import AlertDashboardPage from "./features/system/AlertDashboardPage";
import ChannelSettingsPage from "./features/system/ChannelSettingsPage";
import MedicalStaffPage from "./features/system/MedicalStaffPage";
import SessionManagementPage from "./features/system/SessionManagementPage";
import SystemAlertDisplay from "./components/SystemAlertDisplay";

import DashBoard from "./features/settings/DashBoard";
import TermsPage from "./features/settings/TermsPage";
import TermsHistoryPage from "./features/settings/TermsHistoryPage";
import PrivacyPage from "./features/settings/PrivacyPage";
import PrivacyHistoryPage from "./features/settings/PrivacyHistoryPage";
import PolicyPage from "./features/settings/PolicyPage";
import PopupListPage from "./features/settings/PopupListPage";
import PopupFormPage from "./features/settings/PopupFormPage";
import NoticeListPage from "./features/settings/NoticeListPage";
import NoticeFormPage from "./features/settings/NoticeFormPage";
import FaqListPage from "./features/settings/FaqListPage";
import FaqFormPage from "./features/settings/FaqFormPage";
import FirmwareUploadPage from "./features/settings/FirmwareUploadPage";
import AiModelGlobalPage from "./features/settings/AiModelGlobalPage";

import AccountListPage from "./features/account/AccountListPage";
import SecessionListPage from "./features/account/SecessionListPage";
import FingerprintListPage from "./features/account/FingerprintListPage";

import BluetoothDataPage from "./features/bluetooth/BluetoothDataPage";
import GlucoDataPage from "./features/glucodata/GlucoDataPage";
import ReportListPage from "./features/report/ReportListPage";

import SettingsLayout from "./components/Layout/SettingsLayout";
import AccountLayout from "./components/Layout/AccountLayout";
import BluetoothLayout from "./components/Layout/BluetoothLayout";
import GlucoDataLayout from "./components/Layout/GlucoDataLayout";
import ReportLayout from "./components/Layout/ReportLayout";

import UserDetailLayout from "./components/Layout/UserDetailLayout";
import UserProfilePage from "./features/user/UserProfilePage";
import UserGlucoData from "./features/user/UserGlucoData";
import UserReportPage from "./features/user/UserReportPage";
import UserFingerprintPage from "./features/user/UserFingerprintPage";
import UserAiModelPage from "./features/user/UserAiModelPage";

import AdminAccounts from "./features/system/AdminAccounts";
import CalibrationLayout from "./components/Layout/CalibrationLayout";
import CalibrationQueuePage from "./features/calibration/CalibrationQueuePage";
import CalibrationCasePage from "./features/calibration/CalibrationCasePage";
import AdminLayout from "./components/Layout/AdminLayout";
import ProtectedRoute from "./components/ProtectedRoute";
import { CALIBRATION_ACCESS_ROLES, GENERAL_ADMIN_ROLES } from "./utils/calibrationAdmin";
import { LockContext } from "./context/LockContext";
import LockScreen from "./components/LockScreen";
import IntegrityService from "./services/IntegrityService";

const generalAdminGuard = (element) => (
  <ProtectedRoute allowedRoles={GENERAL_ADMIN_ROLES}>{element}</ProtectedRoute>
);

function App() {
  const { locked } = useContext(LockContext);

  // 무결성 서비스 초기화
  useEffect(() => {
    // IntegrityService는 이미 전역적으로 초기화됨
    console.log("App component mounted with IntegrityService");
  }, []);

  if (locked) return <LockScreen />;
  return (
    <>
      <SystemAlertDisplay />
      <Routes>
        <Route path="/" element={<Navigate replace to="/login" />} />
        <Route path="login" element={<EnhancedLoginPage />} />
        <Route path="password-expired" element={<PasswordExpiredPage />} />
        <Route path="settings/*" element={generalAdminGuard(<SettingsLayout />)}>
          <Route index element={<DashBoard />} />
          <Route path="dashboard" element={<DashBoard />} />
          <Route path="terms" element={<TermsPage />} />
          <Route path="terms/history" element={<TermsHistoryPage />} />
          <Route path="privacy" element={<PrivacyPage />} />
          <Route path="privacy/history" element={<PrivacyHistoryPage />} />
          <Route path="policy" element={<PolicyPage />} />
          <Route path="popup" element={<PopupListPage />} />
          <Route path="popup/:action/:id?" element={<PopupFormPage />} />
          <Route path="notice" element={<NoticeListPage />} />
          <Route path="notice/:action/:id?" element={<NoticeFormPage />} />
          <Route path="firmware" element={<FirmwareUploadPage />} />
          <Route path="ai-model" element={<AiModelGlobalPage />} />
          <Route path="faq" element={<FaqListPage />} />
          <Route path="faq/:action/:id?" element={<FaqFormPage />} />
          <Route path="account" element={<AccountListPage />} />
        </Route>
        <Route path="account/*" element={generalAdminGuard(<AccountLayout />)}>
          <Route index element={<AccountListPage />} />
          <Route path="secession" element={<SecessionListPage />} />
          <Route path="fingerprint" element={<FingerprintListPage />} />
        </Route>
        <Route path="bluetooth/*" element={generalAdminGuard(<BluetoothLayout />)}>
          <Route index element={<BluetoothDataPage />} />
        </Route>
        <Route path="glucodata/*" element={generalAdminGuard(<GlucoDataLayout />)}>
          <Route index element={<GlucoDataPage />} />
        </Route>
        <Route path="report/*" element={generalAdminGuard(<ReportLayout />)}>
          <Route index element={<ReportListPage />} />
        </Route>
        <Route path="user/*" element={generalAdminGuard(<UserDetailLayout />)}>
          <Route path="profile/:userIdx?" element={<UserProfilePage />} />
          <Route path="gluco/:userIdx?" element={<UserGlucoData />} />
          <Route path="report/:userIdx?" element={<UserReportPage />} />
          <Route
            path="fingerprint/:userIdx?"
            element={<UserFingerprintPage />}
          />
          <Route path="ai-model/:userIdx?" element={<UserAiModelPage />} />
        </Route>
        <Route
          path="calibration/*"
          element={(
            <ProtectedRoute allowedRoles={CALIBRATION_ACCESS_ROLES}>
              <CalibrationLayout />
            </ProtectedRoute>
          )}
        >
          <Route index element={<Navigate replace to="/calibration/queue" />} />
          <Route path="queue" element={<CalibrationQueuePage />} />
          <Route path="cases/:caseId" element={<CalibrationCasePage />} />
        </Route>
        <Route
          path="system/*"
          element={
            <ProtectedRoute allowedRoles={["super_admin"]}>
              <AdminLayout />
            </ProtectedRoute>
          }
        >
          <Route
            index
            element={<Navigate replace to="/system/admin-accounts" />}
          />
          <Route path="admin-accounts" element={<AdminAccounts />} />
          <Route path="security" element={<SecurityManagementPage />} />
          <Route path="password-policy" element={<PasswordPolicyPage />} />
          <Route path="calibration-policy" element={<CalibrationPolicyPage />} />
          <Route path="system-alerts" element={<SystemAlertManagementPage />} />
          <Route
            path="mobile-code-policies"
            element={<MobileCodePolicyPage />}
          />
          <Route path="monitoring" element={<SecurityMonitoring />} />
          <Route path="audit-logs" element={<AdminAuditLogPage />} />
          <Route path="audit-capacity" element={<AuditCapacityMonitoring />} />
          <Route path="alert-dashboard" element={<AlertDashboardPage />} />
          <Route path="channel-settings" element={<ChannelSettingsPage />} />
          <Route path="medical-staff" element={<MedicalStaffPage />} />
          <Route path="sessions" element={<SessionManagementPage />} />
        </Route>
      </Routes>
    </>
  );
}

export default App;
